import { useEffect } from 'react'
import { useBlocker } from 'react-router-dom'

/**
 * Blocks in-app navigation and the browser's unload event when `isDirty` is true.
 * Shows a confirm dialog before allowing navigation away.
 *
 * @param isDirty  Whether the form has unsaved changes.
 * @param message  Text shown in the confirm dialog (default provided).
 */
export function useUnsavedChanges(
  isDirty: boolean,
  message = 'You have unsaved changes. Leave anyway?',
): void {
  // Block React Router navigation (link clicks, navigate(), back/forward)
  const blocker = useBlocker(isDirty)

  useEffect(() => {
    if (blocker.state === 'blocked') {
      if (window.confirm(message)) {
        blocker.proceed()
      } else {
        blocker.reset()
      }
    }
  }, [blocker, message])

  // Block browser tab close / hard refresh
  useEffect(() => {
    if (!isDirty) return
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault()
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [isDirty])
}
